import type { AriaLiveMessages, GroupBase, OptionsOrGroups } from "chakra-react-select";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const positionOf = <Option, Group extends GroupBase<Option>>(
  focused: Option,
  options: OptionsOrGroups<Option, Group>,
) => {
  const flat = options.flatMap((entry) =>
    typeof entry === "object" && entry !== null && "options" in entry ? [...(entry as Group).options] : [entry as Option],
  );
  return { position: flat.indexOf(focused) + 1, total: flat.length };
};

export const useSelectScreenReaderMessages = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>() => {
  const { t } = useTranslation();
  return useMemo(() => {
    const ariaLiveMessages: AriaLiveMessages<Option, IsMulti, Group> = {
      guidance: ({ context, isInitialFocus, "aria-label": ariaLabel }) => {
        switch (context) {
          case "menu":
            return t("Use Up and Down to choose, Enter to select the highlighted option and Escape to close the list.");
          case "input": {
            if (!isInitialFocus) return "";
            const label = ariaLabel ?? t("Selector");
            return t("{{label}} is focused. Type to narrow the list, or press Down to open it.", { label });
          }
          case "value":
            return t("Use Left and Right to move between selected values, and Backspace to remove the highlighted one.");
          default:
            return "";
        }
      },
      onChange: ({ action, label, labels, isDisabled }) => {
        if (isDisabled) return t("{{label}} is disabled and can't be selected.", { label });
        switch (action) {
          case "select-option":
            return t("{{label}} selected.", { label });
          case "deselect-option":
          case "pop-value":
          case "remove-value":
            return t("{{label}} removed.", { label });
          case "clear":
            return t("All selections cleared.");
          case "initial-input-focus":
            return t("{{label}} selected.", { label: labels.join(", ") });
          default:
            return "";
        }
      },
      onFilter: ({ inputValue, resultsMessage }) =>
        inputValue
          ? t("{{results}}, search term {{term}}.", { results: resultsMessage, term: inputValue })
          : `${resultsMessage}.`,
      onFocus: ({ context, focused, options, label, isSelected }) => {
        if (context === "value") return t("Value {{label}} is focused.", { label });
        const { position, total } = positionOf<Option, Group>(focused, options);
        return isSelected
          ? t("{{label}}, selected, {{position}} of {{total}}.", { label, position, total })
          : t("{{label}}, {{position}} of {{total}}.", { label, position, total });
      },
    };
    const screenReaderStatus = ({ count }: { count: number }) => t("Results: {{total}}", { total: count });
    return { ariaLiveMessages, screenReaderStatus };
  }, [t]);
};
